import { translate } from "app/language/service"
import { NOT_SAVED_ID } from "app/model"
import { getStore } from "app/store/store"
import { IForm, IFormConfigurationCaps, IFormDataLoader } from "form/model"
import { formActionCreator } from "form/store/actions"
import { FormikErrors } from "formik"
import { CAP_PERIODS, ICap, getAllCapsPeriod } from "../../cap/model"
import { capRPO } from "../../cap/repository"

export async function synchronizeCaps(form: IForm): Promise<void> {
  const {
    tempCampaignId: campaignId,
    configuration: { caps: allCapsToSet }
  } = form
  let { caps: existingCaps } = form.context.relations
  if (!existingCaps) {
    existingCaps = []
  }
  if (typeof campaignId !== "number") {
    return Promise.reject("campaignId must be a number")
  }
  if (campaignId < 0) {
    return Promise.reject("campaignId must be equals or greater then 0")
  }
  if (Array.isArray(existingCaps)) {
    for (const existingCap of existingCaps) {
      if (typeof existingCap !== "object") {
        return Promise.reject("An existing cap is invalid")
      }
    }
  }
  let hasError: boolean = false
  let serverSideCaps: ICap[] = []
  const allCapKeysToSet = Object.keys(allCapsToSet)
  for (const capKeyToSet of allCapKeysToSet) {
    const existingCap: ICap | undefined = existingCaps.find(c => c.period === capKeyToSet)
    const toSetCap: ICap | null = (allCapsToSet as any)[capKeyToSet]
    if (toSetCap === null) {
      // delete
      if (!existingCap || existingCap.id === NOT_SAVED_ID) {
        // should never pass here
        continue
      }
      try {
        await capRPO.deleteCap(campaignId, existingCap.id)
      } catch (error) {
        hasError = true
        console.error("Unable to delete cap", error)
        serverSideCaps.push(existingCap)
      }
      continue
    }
    if (!existingCap || existingCap.id === NOT_SAVED_ID) {
      // create
      try {
        serverSideCaps.push(await capRPO.createCap(campaignId, toSetCap))
      } catch (error) {
        hasError = true
        console.error("Unable to create cap", error)
      }
      continue
    }
    // update
    try {
      serverSideCaps.push(
        await capRPO.updateCap(campaignId, {
          ...toSetCap,
          id: existingCap.id
        })
      )
    } catch (error) {
      hasError = true
      serverSideCaps.push(existingCap)
      console.error("Unable to update cap", error)
    }
  }
  if (hasError) {
    try {
      serverSideCaps = await capRPO.getCaps(form.tempCampaignId)
    } catch (error) {
      // it doesn't smell good here ...
      console.error("Cannot fallback ...", error)
      return
    }
  }
  getStore().dispatch(
    formActionCreator.setRelations(form.context.appContext, { caps: serverSideCaps, capsError: hasError })
  )
}

export function areEqualsValueCaps(cap1: ICap, cap2: ICap) {
  if (!cap1 && !cap2) {
    return true
  }
  if (!cap1 || !cap2) {
    return false
  }
  return cap1.frequency === cap2.frequency && cap1.period === cap2.period && cap1.frequency === cap2.frequency
}

export function isValidCap(cap: ICap): boolean {
  if (typeof cap !== "object") {
    return false
  }
  return cap.units === 1 && typeof cap.frequency === "number" && getAllCapsPeriod().includes(cap.period)
}

export function getCapsInterdependentErrors(caps: IFormConfigurationCaps): FormikErrors<IFormConfigurationCaps> {
  const errors: FormikErrors<IFormConfigurationCaps> = {}
  const monthCap: ICap | null = caps.month
  const weekCap: ICap | null = caps.week
  const dayCap: ICap | null = caps.day
  const hourCap: ICap | null = caps.hour
  const minuteCap: ICap | null = caps.minute
  if (weekCap) {
    if (monthCap && weekCap.frequency * 4 > monthCap.frequency) {
      errors.week = translate("cap.form.invalid.month")
    }
  }
  if (dayCap) {
    if (weekCap && dayCap.frequency * 7 > weekCap.frequency) {
      errors.day = translate("cap.form.invalid.week")
    } else if (monthCap && dayCap.frequency * 28 > monthCap.frequency) {
      // 7 * 4 = 28
      errors.day = translate("cap.form.invalid.month")
    }
  }
  if (hourCap) {
    if (dayCap && hourCap.frequency * 24 > dayCap.frequency) {
      errors.hour = translate("cap.form.invalid.day")
    } else if (weekCap && hourCap.frequency * 168 > weekCap.frequency) {
      // 24 * 7 = 168
      errors.hour = translate("cap.form.invalid.week")
    } else if (monthCap && hourCap.frequency * 672 > monthCap.frequency) {
      // 24 * 7 * 4 = 672
      errors.hour = translate("cap.form.invalid.month")
    }
  }
  if (minuteCap) {
    if (hourCap && minuteCap.frequency * 60 > hourCap.frequency) {
      errors.minute = translate("cap.form.invalid.hour")
    } else if (dayCap && minuteCap.frequency * 1440 > dayCap.frequency) {
      // 60 * 24 = 1440
      errors.minute = translate("cap.form.invalid.day")
    } else if (weekCap && minuteCap.frequency * 10080 > weekCap.frequency) {
      // 60 * 24 * 7 = 10080
      errors.minute = translate("cap.form.invalid.week")
    } else if (monthCap && minuteCap.frequency * 40320 > monthCap.frequency) {
      // 60 * 24 * 7 * 4 = 40320
      errors.minute = translate("cap.form.invalid.month")
    }
  }
  return errors
}

export const getEmptyCap = (period: CAP_PERIODS): ICap => ({
  id: NOT_SAVED_ID,
  frequency: 1,
  period,
  campaign_id: NOT_SAVED_ID,
  units: 1 // this value never change
})

export const capFormDataLoader: IFormDataLoader = async (form: IForm, onFinish: (error?: string) => any) => {
  try {
    const caps = await capRPO.getCaps(form.tempCampaignId)
    form.context.relations.caps = caps.length === 0 ? null : caps
    form.configuration.caps = {
      month: null,
      week: null,
      day: null,
      hour: null,
      minute: null
    }
    for (const cap of caps) {
      form.configuration.caps[cap.period] = cap
    }
    onFinish()
  } catch (error) {
    console.error(error)
    onFinish(`Cap data-loader error ${String(error)}`)
  }
}
